import { AlertTriangle, PackageSearch } from "lucide-react";
import { useParams } from "react-router";

import { EmptyState } from "@/components/ui/empty-state";
import { useFavoriteToggleHandler } from "@/features/engagement/hooks/useFavoriteToggleHandler";
import { useWishlist } from "@/features/engagement/hooks/useWishlist";
import { ProductCard, ProductCardSkeleton } from "@/features/marketplace/components/ProductCard";
import {
  ProfileHeaderCard,
  ProfileHeaderCardSkeleton,
} from "@/features/profile/components/ProfileHeaderCard";
import { usePublicProfile, useSellerListings } from "@/features/profile/hooks/usePublicProfile";
import { ApiError } from "@/types/api";

/**
 * docs/ui/routes.md flagged this exact gap: "no route for viewing another
 * user's public profile is wireframed yet." Public (not `AccountLayout`)
 * deliberately — viewing is open to anyone, including guests, the same
 * "viewing is public, only contacting requires auth" split
 * `ProductDetailsPage` already established. Read-only end to end: nothing
 * on this page can mutate `profile`, only the viewer's own wishlist state.
 *
 * No "Chat with seller" button here — Chat is always scoped to a specific
 * product (`docs/database/relationships.md`: "Product → Chats"), and this
 * page has no single product to attach one to. The real entry point stays
 * "Message Seller" on each listing's own Product Details page, reached by
 * clicking into one of the cards below.
 */
export function PublicProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const profileQuery = usePublicProfile(userId ?? "");
  const listingsQuery = useSellerListings(userId ?? "");
  const wishlistQuery = useWishlist();
  const favoriteIds = new Set(wishlistQuery.data?.map((item) => item.id) ?? []);
  const handleFavoriteToggle = useFavoriteToggleHandler(favoriteIds);

  if (profileQuery.isPending) {
    return (
      <div className="mx-auto max-w-[1000px] space-y-6 px-4 py-8">
        <ProfileHeaderCardSkeleton />
      </div>
    );
  }

  if (profileQuery.isError || !profileQuery.data) {
    const isNotFound = profileQuery.error instanceof ApiError && profileQuery.error.status === 404;
    return (
      <div className="mx-auto max-w-[1000px] px-4 py-8">
        <EmptyState
          icon={AlertTriangle}
          heading={isNotFound ? "User not found" : "Couldn't load this profile"}
          description={
            isNotFound
              ? "This user may no longer exist, or the link is incorrect."
              : "Something went wrong. Please try again."
          }
        />
      </div>
    );
  }

  const profile = profileQuery.data;

  return (
    <div className="mx-auto max-w-[1000px] space-y-6 px-4 py-8">
      <ProfileHeaderCard
        name={profile.name}
        avatarUrl={profile.avatarUrl}
        memberSince={profile.memberSince}
        bio={profile.bio}
        itemsListedCount={profile.itemsListedCount}
      />

      <div>
        <h2 className="mb-4 text-h4 text-foreground">Listings from {profile.name}</h2>

        {listingsQuery.isPending && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        )}

        {listingsQuery.data?.data.length === 0 && (
          <EmptyState icon={PackageSearch} heading="No active listings" description="Check back later." />
        )}

        {listingsQuery.data && listingsQuery.data.data.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {listingsQuery.data.data.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isFavorite={favoriteIds.has(product.id)}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
